import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "wbr/components/footer/Footer";
import LoadingPage from "wbr/LoadingPage"; // ← Import Loading
import "./App.css";
import { useUser } from "shared/context/UserContext";
import { getFunctionOptions } from "shared/api/function_data";
import AutomationSection from "automation/AutomationSection";

function Navbar({ theme, toggleTheme, onAdminClick }) {
  return (
    <nav className="navbar1">
      <div className="navbar1-inner">
        <div className="navbar1-brand">
          <span className="brand1-mark">PULSE</span>
          <span className="brand1-text">SWOTS CIA</span>
        </div>
        <div className="navbar1-actions">
          <button className="admin-btn" onClick={onAdminClick}>
            ⚙ Admin
          </button>
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === "light" ? "Dark" : "Light"}
          </button>
        </div>
      </div>
    </nav>
  );
}

function App() {
  const [theme, setTheme] = useState("light");
  const [functionOptions, setFunctionOptions] = useState([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ NEW: Loading screen state
  const [showLoading, setShowLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const navigate = useNavigate();
  const user = useUser();

  // Track both conditions: 3-second timer + API completion
  const apiDoneRef = useRef(false);
  const timerDoneRef = useRef(false);

  const checkReady = () => {
    if (apiDoneRef.current && timerDoneRef.current) {
      setIsReady(true);
      // Small delay for smooth fade-out transition
      setTimeout(() => {
        setShowLoading(false);
      }, 500);
    }
  };

  // ✅ 0.6-second minimum timer
  useEffect(() => {
    const timer = setTimeout(() => {
      timerDoneRef.current = true;
      checkReady();
    }, 600);

    return () => clearTimeout(timer);
  }, []);

  // ✅ API call runs in background during loading screen
  useEffect(() => {
    const fetchFunctionOptions = async () => {
      try {
        setApiLoading(true);
        setError(null);
        const data = await getFunctionOptions();

        if (Array.isArray(data)) {
          setFunctionOptions(data);
        } else if (typeof data === "object") {
          setFunctionOptions(Object.values(data));
        }
      } catch (err) {
        setError(err.message || "Failed to load function options");
        console.error("Error fetching function options:", err);
      } finally {
        setApiLoading(false);
        apiDoneRef.current = true;
        checkReady();
      }
    };

    fetchFunctionOptions();
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const handleCardClick = (id) => navigate(`/${id}`);
  const handleOnboardClick = () => navigate("/onboard");
  const handleAdminClick = () => navigate("/pulse-admin");

  const handleRetry = async () => {
    try {
      setApiLoading(true);
      setError(null);
      const data = await getFunctionOptions();
      if (Array.isArray(data)) {
        setFunctionOptions(data);
      } else if (typeof data === "object") {
        setFunctionOptions(Object.values(data));
      }
    } catch (err) {
      setError(err.message || "Failed to load function options");
    } finally {
      setApiLoading(false);
    }
  };

  // Error component
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="error-container">
      <p className="error-message">⚠️ {message}</p>
      <button className="retry-btn" onClick={onRetry}>
        Retry
      </button>
    </div>
  );

  return (
    <>
      {/* ✅ LOADING SCREEN — shows for 0.6 sec minimum */}
      {showLoading && <LoadingPage />}

      {/* ✅ MAIN APP — renders behind loading screen, visible after */}
      <div
        className={`app ${theme}`}
        style={{
          opacity: isReady ? 1 : 0,
          transition: "opacity 0.5s ease-in-out",
        }}
      >
        <Navbar
          theme={theme}
          toggleTheme={toggleTheme}
          onAdminClick={handleAdminClick}
        />

        <main className="main-content">
          <section className="hero-section">
            <div className="hero-content">
              <p className="hero-tag">PULSE SUITE</p>
              <h1 className="hero-title">
                Customer Interaction{" "}
                <span className="highlight">Analytics</span>
              </h1>
              <p className="hero-subtitle">
                One place for WBR reviews and Automation — empowering
                data-driven excellence across VRMO SWOTS.
              </p>
            </div>
          </section>

          <section className="analytics-section">
            <div className="section-header">
              <h2 className="section-title">WBR</h2>
            </div>

            {/* Show error state */}
            {error && (
              <ErrorMessage message={error} onRetry={handleRetry} />
            )}

            {/* Show cards when data is loaded */}
            {!error && (
              <div className="cards-grid">
                {functionOptions.map((value) => (
                  <div
                    className="analytics-card clickable-card"
                    key={value.id}
                    onClick={() => handleCardClick(value.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleCardClick(value.id);
                      }
                    }}
                  >
                    <div className="card-accent-bar"></div>
                    <div className="card-body">
                      <div className="card-icon-wrapper">
                        <span className="card-icon-text">{value.label}</span>
                      </div>
                      <p className="card-description">{value.description}</p>
                      <div className="card-footer">
                        <span className="card-explore">{value.type}</span>
                        <span className="card-arrow">→</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Onboard Card */}
                <div
                  className="analytics-card onboard-card clickable-card"
                  onClick={handleOnboardClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleOnboardClick();
                    }
                  }}
                >
                  <div className="card-accent-bar onboard-accent"></div>
                  <div className="card-body">
                    <div className="card-icon-wrapper onboard-icon-wrapper">
                      <span className="card-icon-text">+</span>
                    </div>
                    <div className="card-label-badge onboard-badge">New</div>
                    <p className="card-description">
                      Onboard Your Function to PULSE
                    </p>
                    <div className="card-footer">
                      <span className="card-explore">Get Started</span>
                      <span className="card-arrow">→</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ─── Automation product section (registry-driven) ─── */}
          <AutomationSection
            onAutomationClick={(group) =>
              navigate(`/automation/${group.key}`)
            }
          />

          <section className="team-section">
            <h2 className="section-title">Who We Are</h2>
            <p className="section-subtitle">
              Customer Interaction Analytics Team
            </p>
            <div className="team-intro-card">
              <h3>SWOTS CIA Team</h3>
              <p>
                The Customer Interaction Analytics Team combines Analytics,
                Automation, and MIS expertise to drive data-driven
                decision-making across VRMO SWOTS.
              </p>
            </div>
          </section>
        </main>

        <Footer theme={theme} />
      </div>
    </>
  );
}

export default App;